import React, { useState, useEffect } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { api } from '../../lib/api';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '@/lib/utils';

/**
 * LocationSelector - Searchable cascading dropdown for Governorate → Neighborhood + manual address
 * @param {Object} value - Current location value
 * @param {Function} onChange - Callback when location changes
 * @param {Boolean} required - Whether fields are required
 */
export function LocationSelector({ value = {}, onChange, required = false }) {
  const { t, language, dir } = useI18n();
  const [governorates, setGovernorates] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loadingGovernorates, setLoadingGovernorates] = useState(false);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);
  const [openGov, setOpenGov] = useState(false);
  const [openNeigh, setOpenNeigh] = useState(false);

  // Load governorates on mount
  useEffect(() => {
    loadGovernorates();
  }, []);

  // Load neighborhoods when governorate changes
  useEffect(() => {
    if (value.governorate_en) {
      loadNeighborhoods(value.governorate_en);
    } else {
      setNeighborhoods([]);
    }
  }, [value.governorate_en]);

  const loadGovernorates = async () => {
    setLoadingGovernorates(true);
    try {
      const response = await api.get('/locations/governorates');
      setGovernorates(response.data.items || []);
    } catch (err) {
      console.error('Failed to load governorates:', err);
    } finally {
      setLoadingGovernorates(false);
    }
  };

  const loadNeighborhoods = async (governorate) => {
    setLoadingNeighborhoods(true);
    try {
      const response = await api.get(`/locations/neighborhoods/${governorate}`);
      setNeighborhoods(response.data.items || []);
    } catch (err) {
      console.error('Failed to load neighborhoods:', err);
      setNeighborhoods([]);
    } finally {
      setLoadingNeighborhoods(false);
    }
  };

  const handleGovernorateChange = (selectedValue) => {
    const selected = governorates.find(g => g.value === selectedValue);
    
    onChange({
      ...value,
      governorate_en: selected?.label_en || '',
      governorate_ar: selected?.label_ar || '',
      // Reset neighborhood when governorate changes
      neighborhood_en: '',
      neighborhood_ar: '',
      city_en: selected?.label_en || '',
      city_ar: selected?.label_ar || '',
    });
    setOpenGov(false);
  };

  const handleNeighborhoodChange = (selectedValue) => {
    const selected = neighborhoods.find(n => n.value === selectedValue);
    
    onChange({
      ...value,
      neighborhood_en: selected?.label_en || '',
      neighborhood_ar: selected?.label_ar || '',
    });
    setOpenNeigh(false);
  };

  const handleFieldChange = (field, val) => {
    onChange({ ...value, [field]: val });
  };

  return (
    <div className="space-y-4">
      {/* Governorate Searchable Dropdown */}
      <div>
        <label className="block text-sm font-medium mb-2">
          {t('location.governorate')} {required && <span className="text-red-500">*</span>}
        </label>
        <Popover open={openGov} onOpenChange={setOpenGov}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={openGov}
              className="w-full justify-between"
              disabled={loadingGovernorates}
            >
              {value.governorate_en
                ? (language === 'ar' ? value.governorate_ar : value.governorate_en)
                : t('location.selectGovernorate')}
              <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandInput placeholder={t('location.searchGovernorate')} />
              <CommandEmpty>{t('location.noResults')}</CommandEmpty>
              <CommandGroup className="max-h-64 overflow-auto">
                {governorates.map((gov) => (
                  <CommandItem
                    key={gov.value}
                    value={gov.value}
                    onSelect={handleGovernorateChange}
                  >
                    <Check
                      className={cn(
                        "me-2 h-4 w-4",
                        value.governorate_en === gov.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {language === 'ar' ? gov.label_ar : gov.label_en}
                  </CommandItem>
                ))}
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Neighborhood Searchable Dropdown */}
      {value.governorate_en && (
        <div>
          <label className="block text-sm font-medium mb-2">
            {t('location.neighborhood')} {required && <span className="text-red-500">*</span>}
          </label>
          <Popover open={openNeigh} onOpenChange={setOpenNeigh}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openNeigh}
                className="w-full justify-between"
                disabled={loadingNeighborhoods}
              >
                {value.neighborhood_en
                  ? (language === 'ar' ? value.neighborhood_ar : value.neighborhood_en)
                  : t('location.selectNeighborhood')}
                <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput placeholder={t('location.searchNeighborhood')} />
                <CommandEmpty>{t('location.noResults')}</CommandEmpty>
                <CommandGroup className="max-h-64 overflow-auto">
                  {neighborhoods.map((neigh) => (
                    <CommandItem
                      key={neigh.value}
                      value={neigh.value}
                      onSelect={handleNeighborhoodChange}
                    >
                      <Check
                        className={cn(
                          "me-2 h-4 w-4",
                          value.neighborhood_en === neigh.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {language === 'ar' ? neigh.label_ar : neigh.label_en}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
          {loadingNeighborhoods && (
            <p className="text-xs text-gray-500 mt-1">{t('common.loading')}</p>
          )}
        </div>
      )}

      {/* Manual Address Fields - Only show after neighborhood is selected */}
      {value.neighborhood_en && (
        <>
          {/* Street Address (Bilingual) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('location.streetAddressEn')}
              </label>
              <input
                type="text"
                value={value.address_line_1_en || ''}
                onChange={(e) => handleFieldChange('address_line_1_en', e.target.value)}
                placeholder={t('location.streetAddressPlaceholderEn')}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('location.streetAddressAr')}
              </label>
              <input
                type="text"
                value={value.address_line_1_ar || ''}
                onChange={(e) => handleFieldChange('address_line_1_ar', e.target.value)}
                placeholder={t('location.streetAddressPlaceholderAr')}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                dir="rtl"
              />
            </div>
          </div>

          {/* Floor/Apt Number (Bilingual) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('location.floorAptEn')}
              </label>
              <input
                type="text"
                value={value.floor_suite || ''}
                onChange={(e) => handleFieldChange('floor_suite', e.target.value)}
                placeholder={t('location.floorAptPlaceholderEn')}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('location.floorAptAr')}
              </label>
              <input
                type="text"
                value={value.floor_suite_ar || ''}
                onChange={(e) => handleFieldChange('floor_suite_ar', e.target.value)}
                placeholder={t('location.floorAptPlaceholderAr')}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                dir="rtl"
              />
            </div>
          </div>

          {/* Additional Notes / Landmarks (Bilingual) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('location.additionalNotesEn')} <span className="text-gray-500 text-xs">({t('common.optional')})</span>
              </label>
              <input
                type="text"
                value={value.address_line_2_en || ''}
                onChange={(e) => handleFieldChange('address_line_2_en', e.target.value)}
                placeholder={t('location.additionalNotesPlaceholderEn')}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('location.additionalNotesAr')} <span className="text-gray-500 text-xs">({t('common.optional')})</span>
              </label>
              <input
                type="text"
                value={value.address_line_2_ar || ''}
                onChange={(e) => handleFieldChange('address_line_2_ar', e.target.value)}
                placeholder={t('location.additionalNotesPlaceholderAr')}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                dir="rtl"
              />
            </div>
          </div>

          {/* Google Maps URL (Optional) */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('location.googleMapsUrl')} <span className="text-gray-500 text-xs">({t('common.optional')})</span>
            </label>
            <input
              type="url"
              value={value.google_maps_url || ''}
              onChange={(e) => handleFieldChange('google_maps_url', e.target.value)}
              placeholder="https://goo.gl/maps/..."
              className="w-full px-3 py-2 border border-input rounded-md bg-background"
            />
            <p className="text-xs text-gray-500 mt-1">{t('location.googleMapsHint')}</p>
          </div>
        </>
      )}
    </div>
  );
}

